/**
 * Filtered GPS Data Service for FlowRide
 * Applies Kalman filtering to raw GPS data for smoother and more accurate position tracking
 * Subscribes to RawGpsDataService and emits filtered data
 */

import { createGenevaFilteredGpsData, GENEVA_DEFAULT_POSITION } from "../../tools/GenevaDefaults";
import {
    FilteredGpsData,
    GpsData,
    NavigationEventType,
    RawGpsData,
    type NavigationEvent,
} from "../../types/Navigation";
import { LoggerService } from "../LoggerService";
import { CustomKalmanFilter, KalmanFilterFactory } from "./CustomKalmanFilter";
import { rawGpsDataService } from "./RawGpsDataService";

/**
 * Event listener callback type
 */
type FilteredGpsEventListener = ( data: FilteredGpsData ) => void;

/**
 * Service that filters raw GPS data using Kalman filtering
 */
export class FilteredGpsDataService {

    /** Singleton instance */
    private static instance: FilteredGpsDataService | null = null;

    /** Last filtered GPS data */
    private lastValue: FilteredGpsData = createGenevaFilteredGpsData();

    /** Event listeners */
    private listeners: Set<FilteredGpsEventListener> = new Set();

    /** Custom Kalman filter for latitude */
    private latitudeFilter: CustomKalmanFilter | null = null;

    /** Custom Kalman filter for longitude */
    private longitudeFilter: CustomKalmanFilter | null = null;

    /** Custom Kalman filter for heading */
    private headingFilter: CustomKalmanFilter | null = null;

    /** Custom Kalman filter for speed */
    private speedFilter: CustomKalmanFilter | null = null;

    /** Whether the service is active */
    private active: boolean = false;

    /** Unsubscribe function from raw GPS service */
    private unsubscribeFromRawGps: ( () => void ) | null = null;

    /** Previous raw GPS data for calculating heading */
    private previousRawData: RawGpsData | null = null;

    /** Minimum distance for heading calculation (meters) */
    private readonly MIN_DISTANCE_FOR_HEADING = 1; // Reduced from 5m to 1m for better heading detection

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        this.initializeFilters();
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): FilteredGpsDataService {
        if ( FilteredGpsDataService.instance === null ) {
            FilteredGpsDataService.instance = new FilteredGpsDataService();
        }

        return FilteredGpsDataService.instance;
    }

    /**
     * Get the last filtered GPS data
     * @returns Last filtered data or null if no data available
     */
    public getLastValue(): FilteredGpsData {
        return this.lastValue;
    }

    /**
     * Initialize custom Kalman filters
     */
    private initializeFilters(): void {
        // Create filters using our custom implementation
        this.latitudeFilter = KalmanFilterFactory.createPositionFilter();
        this.longitudeFilter = KalmanFilterFactory.createPositionFilter();
        this.speedFilter = KalmanFilterFactory.createSpeedFilter();
        this.headingFilter = KalmanFilterFactory.createHeadingFilter();

        // console.log( "[FilteredGpsDataService] Custom Kalman filters initialized" );
    }

    /**
     * Start filtering GPS data
     */
    public start( lastGpsData: GpsData ): void {
        if ( this.active ) {
            console.warn( "[FilteredGpsDataService] Already active" );

            return;
        }

        // Initialize lastValue with persisted lastGpsData slice or Geneva default
        this.lastValue = {
            ...lastGpsData,
            "altitude": GENEVA_DEFAULT_POSITION.altitude,
        } as FilteredGpsData;

        this.active = true;

        // Subscribe to raw GPS data
        this.unsubscribeFromRawGps = rawGpsDataService.subscribe( this.handleRawGpsData.bind( this ) );

        // console.log( "[FilteredGpsDataService] Started GPS filtering" );
    }

    /**
     * Stop filtering GPS data
     */
    public stop(): void {
        if ( this.unsubscribeFromRawGps !== null ) {
            this.unsubscribeFromRawGps();
            this.unsubscribeFromRawGps = null;
        }

        this.active = false;
        this.previousRawData = null;

        // Reset filters
        this.initializeFilters();

        // console.log( "[FilteredGpsDataService] Stopped GPS filtering" );
    }

    /**
     * Subscribe to filtered GPS updates
     * @param listener - Callback function
     * @returns Unsubscribe function
     */
    public subscribe( listener: FilteredGpsEventListener ): () => void {
        this.listeners.add( listener );

        // Immediately send last value if available
        if ( this.lastValue !== null ) {
            listener( this.lastValue );
        }

        return () => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Handle raw GPS data and apply custom Kalman filtering
     * @param rawData - Raw GPS data
     */
    private handleRawGpsData( rawData: RawGpsData ): void {
        console.log( "[FilteredGpsDataService] Received raw GPS data", {
            "latitude":  rawData.latitude,
            "longitude": rawData.longitude,
            "timestamp": rawData.timestamp,
        } );

        // Validate GPS data before processing
        if (
            rawData.latitude === null ||
            rawData.latitude === undefined ||
            rawData.longitude === null ||
            rawData.longitude === undefined
        ) {
            LoggerService.warn( "Invalid GPS data received", "FilteredGpsDataService", {
                "latitude":  rawData.latitude,
                "longitude": rawData.longitude,
            } );

            return;
        }

        // Check if filters are properly initialized
        if (
            this.latitudeFilter === null ||
            this.longitudeFilter === null ||
            this.speedFilter === null ||
            this.headingFilter === null
        ) {
            LoggerService.error( "Custom Kalman filters not initialized", "FilteredGpsDataService", {
                "latitudeFilter":  this.latitudeFilter,
                "longitudeFilter": this.longitudeFilter,
                "speedFilter":     this.speedFilter,
                "headingFilter":   this.headingFilter,
            } );

            return;
        }

        // Apply custom Kalman filtering
        let filteredLatitude: number;
        let filteredLongitude: number;
        let filteredSpeed: number | null = null;
        let filteredHeading: number | null = null;

        try {
            // Apply Kalman filtering to position
            filteredLatitude = this.latitudeFilter.update( rawData.latitude );
            filteredLongitude = this.longitudeFilter.update( rawData.longitude );

            // Apply Kalman filtering to speed if available
            if ( rawData.speed !== null && rawData.speed >= 0 ) {
                filteredSpeed = this.speedFilter.update( rawData.speed );
                filteredSpeed = Math.max( 0, filteredSpeed ); // Ensure non-negative
            }

            // Calculate heading from movement if GPS heading is not available or unreliable
            if ( this.previousRawData !== null ) {
                const distance = this.calculateDistance(
                    this.previousRawData.latitude,
                    this.previousRawData.longitude,
                    rawData.latitude,
                    rawData.longitude,
                );

                if ( distance > this.MIN_DISTANCE_FOR_HEADING ) {
                    const calculatedHeading = this.calculateBearing(
                        this.previousRawData.latitude,
                        this.previousRawData.longitude,
                        rawData.latitude,
                        rawData.longitude,
                    );

                    // Use calculated heading if GPS heading is not available
                    if ( rawData.heading === null || rawData.heading === undefined ) {
                        filteredHeading = calculatedHeading;

                        /*
                         *  LoggerService.debug(
                         *     "Using calculated heading (no GPS heading)",
                         *     "FilteredGpsDataService",
                         *     {
                         *         calculatedHeading,
                         *         distance,
                         *     },
                         * );
                         */
                    } else {
                        // Blend GPS heading with calculated heading
                        filteredHeading = this.blendHeadings(
                            rawData.heading,
                            calculatedHeading,
                            0.7,
                        );
                    }
                } else {
                    // Use GPS heading if available
                    filteredHeading = rawData.heading;
                }
            } else {
                // First reading - use GPS heading if available
                filteredHeading = rawData.heading;
            }

            // Apply Kalman filtering to heading if available
            if ( filteredHeading !== null && filteredHeading !== undefined ) {
                filteredHeading = this.headingFilter.update( filteredHeading );

                // Normalize to [0, 360)
                filteredHeading = ( ( filteredHeading % 360 ) + 360 ) % 360;
            } else {
                /*
                 * LoggerService.debug(
                 *     "No heading available for filtering",
                 *     "FilteredGpsDataService",
                 *     {
                 *         "rawHeading": rawData.heading,
                 *         filteredHeading,
                 *     },
                 * );
                 */
            }
        } catch( error ) {
            LoggerService.error( "Custom Kalman filter error", "FilteredGpsDataService", {
                "error":   error instanceof Error ? error.message : "Unknown error",
                "rawData": {
                    "latitude":      rawData.latitude,
                    "longitude":     rawData.longitude,
                    "latitudeType":  typeof rawData.latitude,
                    "longitudeType": typeof rawData.longitude,
                },
            } );

            return;
        }

        // Create filtered data
        const filteredData: FilteredGpsData = {
            "latitude":  filteredLatitude,
            "longitude": filteredLongitude,
            "accuracy":  rawData.accuracy * 0.8, // Filtering improves accuracy
            "heading":   filteredHeading,
            "speed":     filteredSpeed,
            "timestamp": rawData.timestamp,
            "altitude":  rawData.altitude,
        } as FilteredGpsData;

        // Store for next iteration
        this.previousRawData = rawData;

        // Store last value
        this.lastValue = filteredData;

        // Emit to listeners
        this.emit( filteredData );
    }

    /**
     * Calculate distance between two points using Haversine formula
     * @param lat1 - Latitude of first point
     * @param lon1 - Longitude of first point
     * @param lat2 - Latitude of second point
     * @param lon2 - Longitude of second point
     * @returns Distance in meters
     */
    private calculateDistance( lat1: number, lon1: number, lat2: number, lon2: number ): number {
        const R = 6371000; // Earth's radius in meters
        const φ1 = ( lat1 * Math.PI ) / 180;
        const φ2 = ( lat2 * Math.PI ) / 180;
        const Δφ = ( ( lat2 - lat1 ) * Math.PI ) / 180;
        const Δλ = ( ( lon2 - lon1 ) * Math.PI ) / 180;

        const a =
            Math.sin( Δφ / 2 ) * Math.sin( Δφ / 2 ) +
            Math.cos( φ1 ) * Math.cos( φ2 ) * Math.sin( Δλ / 2 ) * Math.sin( Δλ / 2 );

        const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

        return R * c;
    }

    /**
     * Calculate bearing between two points
     * @param lat1 - Latitude of first point
     * @param lon1 - Longitude of first point
     * @param lat2 - Latitude of second point
     * @param lon2 - Longitude of second point
     * @returns Bearing in degrees [0, 360)
     */
    private calculateBearing( lat1: number, lon1: number, lat2: number, lon2: number ): number {
        const φ1 = ( lat1 * Math.PI ) / 180;
        const φ2 = ( lat2 * Math.PI ) / 180;
        const Δλ = ( ( lon2 - lon1 ) * Math.PI ) / 180;

        const y = Math.sin( Δλ ) * Math.cos( φ2 );
        const x = Math.cos( φ1 ) * Math.sin( φ2 ) - Math.sin( φ1 ) * Math.cos( φ2 ) * Math.cos( Δλ );

        const θ = Math.atan2( y, x );
        const bearing = ( ( θ * 180 ) / Math.PI + 360 ) % 360;

        return bearing;
    }

    /**
     * Blend two headings with circular interpolation
     * @param heading1 - First heading in degrees
     * @param heading2 - Second heading in degrees
     * @param weight1 - Weight for first heading [0, 1]
     * @returns Blended heading in degrees [0, 360)
     */
    private blendHeadings( heading1: number, heading2: number, weight1: number ): number {
        const weight2 = 1 - weight1;

        // Convert to radians
        const h1Rad = ( heading1 * Math.PI ) / 180;
        const h2Rad = ( heading2 * Math.PI ) / 180;

        // Convert to unit vectors
        const x = weight1 * Math.cos( h1Rad ) + weight2 * Math.cos( h2Rad );
        const y = weight1 * Math.sin( h1Rad ) + weight2 * Math.sin( h2Rad );

        // Convert back to degrees
        const blended = ( Math.atan2( y, x ) * 180 ) / Math.PI;

        return ( blended + 360 ) % 360;
    }

    /**
     * Emit filtered GPS data to all listeners
     * @param data - Filtered GPS data
     */
    private emit( data: FilteredGpsData ): void {
        const event: NavigationEvent<FilteredGpsData> = {
            "type":      NavigationEventType.FILTERED_GPS_UPDATE,
            data,
            "timestamp": data.timestamp,
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[FilteredGpsDataService] Error in listener:", error );
            }
        } );
    }

    /**
     * Check if service is active
     * @returns True if active
     */
    public isActive(): boolean {
        return this.active;
    }

    /**
     * Get number of active listeners
     * @returns Number of listeners
     */
    public getListenerCount(): number {
        return this.listeners.size;
    }

}

/**
 * Export singleton instance for convenience
 */
export const filteredGpsDataService = FilteredGpsDataService.getInstance();
